import { ChevronRight } from '@mui/icons-material';
import { CircularProgress, Typography } from '@mui/material';
import classnames from 'classnames';
import { ModalPreviewSamples } from 'js/components/destination/destination-content/database/ModalPreviewSamples';
import TableUpdateMode from 'js/components/destination/destination-content/database/TableUpdateMode';
import ResizableHorizontalLayout from 'js/components/elements/ResizableHorizontalLayout';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import { SUPPORT_KEY_SINK_FORM_DB } from 'js/constants/supportPageKeys';
import { convertToValidDBColumnName, getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import {
  cloneDeep,
  filter,
  findKey,
  forEach,
  get,
  has,
  isEmpty,
  keys,
  map,
  reduce,
  toLower,
} from 'lodash';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { PROBE_GET_TREE_REQUEST } from 'js/constants/actionTypes';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-rr-refresh.svg';
import { Box } from 'new/elements/Box';
import { EmptyView } from 'new/elements/EmptyView';
import { ExpandableSearchBar } from 'new/elements/ExpandableSearchBar';
import { NavScroller } from 'new/elements/NavScroller';
import { NavScrollerContext } from 'new/elements/NavScroller/NavScroller';
import { SearchableProvider } from 'new/elements/SearchableProvider';
import { Stack } from 'new/elements/Stack';
import { tryCatchAsync } from 'new/utils/TryCatch';
import { UiMode } from 'new/constants/uiMode';
import React from 'react';
import { ResourcePopoverMenu } from 'src/new/elements/ResourcePopoverMenu';
import Constants from '../../../../Constants';
import JSONUtils from '../../../../lib/JSONUtils';
import HeaderMetaConfiguration from '../../HeaderMetaConfiguration/HeaderMetaConfiguration';
import AdvancedConfiguration from '../AdvancedConfiguration';
import DatabaseList from './DatabaseList';
import TableEditor from './TableEditor';
import TableList from './TableList';
import { ReactComponent as DatabaseIcon } from './assets/database.svg';
import { ReactComponent as TableIcon } from './assets/table.svg';

import 'js/components/data_sources/add_ds_form/SelectBucket.scss';
import './DatabaseConfigurator.scss';

const initialState = {
  databasesIds: [],
  databases: {},
  tables: {},
  selectedDatabaseId: null,
  expandedTableId: null,
  tablesForDatabase: {},
  showPreview: false,
};

const initNavOptions = [{ label: 'Destination Table', id: 'skin-destination-table' }];

const fullNavOptions = [
  { label: 'Destination Table', id: 'skin-destination-table' },
  {
    label: 'Update Mode',
    id: 'skin-update-mode',
  },
  {
    label: 'Format Table',
    id: 'skin-format-table',
  },
];

function TableListWithProbing(props) {
  const isProbingTree = useIsLoading(PROBE_GET_TREE_REQUEST);
  return <TableList {...props} isProbingTree={isProbingTree} />;
}

class DatabaseConfigurator extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      databasesIds: [],
      tablesIds: [],
      databases: {},
      tables: {},
      selectedDatabaseId: null,
      expandedTableId: null,
      tablesForDatabase: {},
      configFields: [],
      insertModeField: {},
      showPreview: false,
      loadingPreview: false,
      selectedTableForPreview: null,
    };
  }

  componentDidMount() {
    let fieldsForThisConnection = this.props.sinkConfigFields
      ? this.props.selectedConnection
        ? this.props.sinkConfigFields[this.props.selectedConnection]
        : this.props.sinkConfigFields[0]
      : null;

    //  Resetting table name on going back to configre from format screen in case of new table name
    if (
      !!this.props.sinkConfig?.database &&
      this.props.isNewTable &&
      this.props.type === 'configure' &&
      !!this.props.sinkConfig.table
    ) {
      this.props.updateFormData(this.props.formType, {
        selectedTable: {
          ...this.props.selectedTable,
          id: this.props.selectedTable.database + '/New Table',
          name: '',
        },
        isNewTable: true,
        sinkConfig: {
          ...this.props.sinkConfig,
          table: '',
        },
      });
    }

    let configFields = filter(
      fieldsForThisConnection,
      (o) =>
        [
          'table',
          'firebolt.table.type',
          'mapping',
          'insert.mode',
          'primary.key',
          'upsert.nulls',
          'pipeline.type',
          'partitioning.column',
          'clustering.columns',
          'cleanroom.consumer.accountname',
          'cleanroom.join.keys',
          'cleanroom.dimensions',
          ...Constants.HC_SINK_FIELD_NAMES,
        ].indexOf(o.name) === -1,
    );
    let insertModeField = filter(fieldsForThisConnection, (o) => o.name === 'insert.mode').shift();

    if (this.props.selectedCredentialId) {
      //Fetch probe tree for this data source
      this.setState(
        {
          ...initialState,
          configFields,
          insertModeField,
        },
        () => {
          this.onRequestTree();
        },
      );
    } else {
      this.setState({
        ...initialState,
        configFields,
        insertModeField,
      });
    }

    this.props.loadDBDataTypes(this.props.selectedConnection);

    if (this.props.setContextualSupport) {
      this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_DB);
    }
  }

  componentDidUpdate(prevProps) {
    let {
      addedDataSinkId,
      selectedConnection,
      selectedCredentialId,
      selectedDatabase,
      selectedTable,
      sinkConfig,
      isEditMode,
      isNewTable,
      dataSchema,
      probeData,
      probeDataFlat,
      schemaToDBDataTypes,
    } = this.props;

    if (prevProps.selectedCredentialId !== selectedCredentialId) {
      this.props.onRequestProbeTree();
    }

    if (
      selectedTable &&
      dataSchema.length &&
      (!prevProps.selectedTable || isNewTable !== prevProps.isNewTable)
    ) {
      if (!selectedTable.columns.length && isNewTable) {
        this.editSelectedTable(
          'columns',
          map(dataSchema, (s) => ({
            attr: s.__txName,
            name: convertToValidDBColumnName(s.__name, selectedConnection),
            type: get(
              schemaToDBDataTypes,
              toLower(s.__format),
              get(schemaToDBDataTypes, s.__type, schemaToDBDataTypes.unknown),
            ),
          })),
        );
      } else if (selectedTable.columns.length && !isNewTable) {
        let st = cloneDeep(selectedTable),
          edited = false;
        forEach(st.columns, (c) => {
          const lowerCaseColumnName = c.name.toLowerCase();

          if (isEditMode && addedDataSinkId) {
            const attrName = findKey(sinkConfig.mapping.mapping, (columnNameToTypeMap) =>
              map(keys(columnNameToTypeMap), (name) => name.toLowerCase()).includes(
                lowerCaseColumnName,
              ),
            );

            if (attrName && !c.attr && c.attr !== attrName) {
              c.attr = attrName;
              edited = true;
            }
          } else {
            const attr = filter(
              dataSchema,
              (s) => lowerCaseColumnName === s.__name.toLowerCase(),
            ).shift();

            if (attr && !c.attr && c.attr !== attr.__txName) {
              c.attr = attr.__txName;
              edited = true;
            }
          }
        });

        if (edited) {
          this.editSelectedTable('columns', st.columns);
        }
      }
    }

    if (probeData && probeData !== prevProps.probeData) {
      let databasesIds = [],
        databases = {},
        tables = {},
        tablesForDatabase = {},
        tableNamesByDatabase = {};

      if (get(probeData, '[0].type') === 'database') {
        forEach(probeData, (d) => {
          let database = {
            id: d.id,
            name: d.name,
          };
          databasesIds.push(d.id);
          databases[d.id] = database;
          tablesForDatabase[d.id] = [];
          tableNamesByDatabase[d.id] = [];
          forEach(d.children, (t) => {
            let table = {
              id: t.id,
              name: t.name,
              database: d.name,
              columns: t.children,
            };
            if (sinkConfig['insert.mode']) {
              table['insert.mode'] = sinkConfig['insert.mode'];
            }
            table['primary.key'] = sinkConfig['primary.key'];
            if (has(sinkConfig, 'upsert.nulls')) {
              table['upsert.nulls'] = sinkConfig['upsert.nulls'];
            }
            tables[t.id] = table;
            tablesForDatabase[d.id].push(t.id);
            tableNamesByDatabase[d.id].push(t.name);
          });
        });

        let updatingSelectedTable = false;
        if (has(sinkConfig, 'table')) {
          const tId = sinkConfig.database + '/' + sinkConfig.table;

          if (has(sinkConfig, 'table') && !!sinkConfig.table && has(tables, tId)) {
            updatingSelectedTable = true;
            this.props.updateFormData(prevProps.formType, {
              selectedTable: cloneDeep(tables[tId]),
            });
          }
        }

        this.setState(
          {
            databasesIds,
            databases,
            tables,
            tablesForDatabase,
            selectedDatabaseId: selectedDatabase || this.state.selectedDatabaseId,
          },
          () => {
            const nodePath = sinkConfig.database + '/' + sinkConfig.table;

            if (
              has(this.props, 'sinkConfig') &&
              (has(sinkConfig, 'table') || has(sinkConfig, 'name')) &&
              probeDataFlat[nodePath] &&
              probeDataFlat[nodePath].scanned &&
              (!selectedTable || (selectedTable && updatingSelectedTable))
            ) {
              this.openTableEditor(nodePath, sinkConfig.mapping, isNewTable);
            }

            // required for some form validations
            this.props.updateFormData(prevProps.formType, {
              tableNamesByDatabase,
            });
          },
        );
      } else {
        this.setState({
          ...initialState,
        });
      }
    }

    if (
      !isEmpty(this.state.tables) &&
      !this.state.selectedDatabaseId &&
      sinkConfig.database &&
      sinkConfig.table &&
      !this.state.tables[sinkConfig.database + '/' + sinkConfig.table]
    ) {
      // this is the case when a new table is created and data insertion is still pending,
      // in this case, table will not come in the tables list.
      this.openTableEditor(
        sinkConfig.database + '/' + sinkConfig.table,
        sinkConfig.mapping,
        this.props.isNewTable || false,
        true,
      );
    }

    if (selectedDatabase !== prevProps.selectedDatabase) {
      this.setState({ selectedDatabaseId: selectedDatabase });
    }
  }

  componentWillUnmount() {
    if (this.props.selectedTable && this.props.selectedTable.columns) {
      let editedColumns = filter(this.props.selectedTable.columns, (c) => c.attr);
      let newMapping = reduce(
        editedColumns,
        (result, c) => {
          return {
            ...result,
            [c.attr]: { ...result[c.attr], [c.name]: c.type },
          };
        },
        {},
      );
      this.props.onSinkMappingUpdated(JSONUtils.stringifyJson(newMapping));
    }

    if (this.props.unsetContextualSupport) {
      this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_DB);
    }
  }

  selectDatabase = (dId) => {
    if (dId === this.state.selectedDatabaseId) {
      this.setState(
        {
          selectedDatabaseId: null,
        },
        () => {
          this.props.onTableSelected(null);
        },
      );
    } else {
      this.probeNode(dId);

      this.setState({
        selectedDatabaseId: dId,
      });
    }
  };

  expandTable = (tId) => {
    this.probeNode(tId);

    this.setState({
      expandedTableId: tId,
    });
  };

  openTableEditor = (tId, mapping, isNewTable = false, moveToNextStep = false) => {
    if (tId) {
      let table = this.state.tables[tId];
      if (table) {
        const hasMapping = mapping && !isEmpty(mapping.mapping);
        let attrNames;
        let attrIndexByName;

        if (!hasMapping) {
          attrIndexByName = {};
          attrNames = [];
          forEach(this.props.dataSchema, (attr, index) => {
            const lowerCaseAttrName = attr.__name.toLowerCase();
            attrNames.push(lowerCaseAttrName);
            attrIndexByName[lowerCaseAttrName] = index;
          });
        }

        forEach(table.columns, (c) => {
          // case-insensitive search because Redshift column names are lower case
          const lowerCaseColumnName = c.name.toLowerCase();

          if (hasMapping) {
            c.attr = this.props.dataSchema.find(
              (attr) => attr.__name.toLowerCase() === lowerCaseColumnName,
            )?.__name;
            // Removing this in order to allow any custom entry
            // if (!find(this.props.dataSchema, { __name: c.attr })) {
            //   c.attr = undefined;
            // }
          } else if (!isEmpty(attrNames)) {
            const index = attrNames.indexOf(lowerCaseColumnName);

            if (index > -1) {
              c.attr = this.props.dataSchema[attrIndexByName[lowerCaseColumnName]].__name;
              attrNames.splice(index, 1);
            }
          }
        });
        if (!table['insert.mode']) {
          table['insert.mode'] = this.state.insertModeField.default;
        }
        this.probeNode(tId);

        this.setState(
          {
            expandedTableId: tId,
            tableNotFound: false,
          },
          () => {
            this.props.onTableSelected(table, isNewTable);
          },
        );
      } else {
        this.setState(
          {
            expandedTableId: null,
            tableNotFound: true,
          },
          () => {
            this.props.onTableSelected(null, isNewTable);
          },
        );
      }
    } else {
      this.setState({ tableNotFound: false }, () => {
        if (
          (this.props.selectedTable || this.state.selectedDatabaseId) &&
          this.props.selectedTable?.id !== this.state.selectedDatabaseId + '/New Table'
        ) {
          this.props.onTableSelected(
            {
              database: this.state.selectedDatabaseId,
              'insert.mode': this.state.insertModeField.default,
              columns: [],
              name: '',
              id: this.state.selectedDatabaseId + '/New Table',
            },
            true,
          );
        }
      });
    }
  };

  editSelectedTable = (key, value) => {
    let st = cloneDeep(this.props.selectedTable);
    st[key] = value;
    if (key === 'name') {
      st.id = st.database + '/' + value;
    }
    this.props.onTableSelected(st, this.props.isNewTable);
  };

  editSelectedTableColumns = (ix, key, value, extra) => {
    let st = cloneDeep(this.props.selectedTable);

    let effectiveValue = value;
    if (key === 'name' && value) {
      effectiveValue = convertToValidDBColumnName(value, this.props.selectedConnection);
    }

    switch (key) {
      case 'partitioning.column':
        {
          if (effectiveValue) {
            const columnName = st.columns[ix].name;
            st['partitioning.column'] = columnName;
          } else {
            st['partitioning.column'] = '';
          }
        }
        break;

      case 'clustering.columns':
      case 'cleanroom.dimensions':
      case 'cleanroom.join.keys':
        {
          //for columns which support muiltiple selections that are saved as a joined string.
          const columnsVal = isEmpty(st[key]) ? [] : st[key].split(',');
          const columnName = st.columns[ix].name;
          if (effectiveValue) {
            columnsVal.push(columnName);
          } else {
            const keyIndex = columnsVal.indexOf(columnName);
            if (keyIndex > -1) {
              columnsVal.splice(keyIndex, 1);
            }
          }
          st[key] = columnsVal.join(',');
        }
        break;

      case 'primaryKey':
        {
          const primaryKeys = isEmpty(st['primary.key']) ? [] : st['primary.key'].split(',');
          const columnName = st.columns[ix].name;

          if (effectiveValue) {
            primaryKeys.push(columnName);
          } else {
            const primaryKeyIndex = primaryKeys.indexOf(columnName);

            if (primaryKeyIndex > -1) {
              primaryKeys.splice(primaryKeyIndex, 1);
            }
          }

          st['primary.key'] = primaryKeys.join(',');
        }
        break;

      default:
        st.columns[ix][key] = effectiveValue;
        break;
    }

    if (key === 'attr' && !st.columns[ix].name) {
      st.columns[ix].name = extra;
    }
    this.props.onTableSelected(st, this.props.isNewTable);
  };

  addSelectedTableColum = () => {
    let st = cloneDeep(this.props.selectedTable);
    st.columns.push({});
    this.props.updateFormData(this.props.formType, {
      selectedTable: st,
    });
  };

  removeSelectedTableColum = (ix) => {
    let st = cloneDeep(this.props.selectedTable);
    st.columns.splice(ix, 1);
    this.props.updateFormData(this.props.formType, {
      selectedTable: st,
    });
  };

  clearColumnAttribute = (ix) => {
    let st = cloneDeep(this.props.selectedTable);
    st.columns[ix].attr = '';
    this.props.updateFormData(this.props.formType, {
      selectedTable: st,
    });
  };

  probeNode = (nodeId) => {
    const node = this.props.probeDataFlat[nodeId];

    if (node && !node.scanned) {
      this.props.onRequestProbeTree(false, node);
    }
  };

  onTrackerModeChanged = (_, isChecked) => {
    const { sinkConfig } = this.props;
    const mapping = { ...get(sinkConfig, 'mapping', {}) };

    if (isChecked) {
      mapping.tracker_mode = 'RECORD';
    } else {
      mapping.tracker_mode = 'NONE';
      delete mapping.tracker_name;
    }

    this.props.onSinkConfigChanged({
      ...sinkConfig,
      mapping,
    });
  };

  onTrackerNameChanged = (value) => {
    const { sinkConfig, selectedConnection } = this.props;
    const mapping = {
      ...get(sinkConfig, 'mapping', {}),
      tracker_name: convertToValidDBColumnName(value, selectedConnection),
    };

    this.props.onSinkConfigChanged({
      ...sinkConfig,
      mapping,
    });
  };

  onAutoModeChanged = (_, value) => {
    const { sinkConfig } = this.props;
    let mappingMode = value ? { mode: 'auto', tracker_mode: 'NONE' } : { mode: 'manual' };

    this.props.onSinkConfigChanged({
      ...sinkConfig,
      mapping: mappingMode,
    });
  };

  onChange = ({ name, value }) => {
    const { sinkConfig } = this.props;
    this.props.onSinkConfigChanged({
      ...sinkConfig,
      [name]: value,
    });
  };

  handleShowPreview = (state = true, tableName) => {
    this.setState({
      showPreview: state,
      selectedTableForPreview: tableName,
    });
  };

  handleLoadingPreview = (state = true) => {
    this.setState({ loadingPreview: state });
  };

  validateRequiredFields = (config) => {
    const { selectedConnection, sinkConfigFields } = this.props;
    const fields = sinkConfigFields[selectedConnection];
    const missingFields = getEmptyRequiredFields(fields, config);
    return !missingFields.length;
  };

  dispatchAdvancedModeSave = async (sinkConfigString) => {
    const { nextFormStage, onSaveAdvancedMode, onError } = this.props;

    if (!JSONUtils.isJSON(sinkConfigString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const { name, description, sink_config } = JSON.parse(sinkConfigString);

    const data = {
      ...sink_config,
      name,
      description,
      'ui.mode': UiMode.CODE,
    };

    const { error } = await tryCatchAsync(onSaveAdvancedMode(data));
    if (error) {
      onError(error.message);
      return;
    }
    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { sinkConfig, onSinkConfigChanged } = this.props;
    if (typeof onSinkConfigChanged !== 'function') return;

    onSinkConfigChanged({ ...sinkConfig, 'ui.mode': UiMode.VIEW });
  };

  getResourceConfig = () => {
    const { addedDataSink, sinkConfig } = this.props;
    const name = addedDataSink?.name || sinkConfig?.name || '';
    const description = addedDataSink?.description || sinkConfig?.description || '';
    return {
      name,
      description,
      sink_config: {
        table: null,
        database: null,
        ...sinkConfig,
      },
    };
  };

  onRequestTree = () => {
    if (this.props.sinkConfig && this.props.sinkConfig.database && this.props.sinkConfig.table) {
      this.props.onRequestInitialDBProbeTree({
        type: 'table',
        name: this.props.sinkConfig.table,
        parent: this.props.sinkConfig.database,
      });
    } else {
      this.props.onRequestProbeTree();
    }
  };

  render() {
    let {
      databasesIds,
      tablesIds,
      databases,
      tables,
      selectedDatabaseId,
      expandedTableId,
      tablesForDatabase,
      configFields,
      insertModeField,
      selectedTableForPreview,
    } = this.state;
    let {
      dataSet,
      sinkConfig,
      flowType,
      addedDataSink,
      currentOrg,
      type,
      selectedTable,
      dataSchema,
      probeDataFlat,
      isLoadingProbeData,
      connectionMetas,
      dbDataTypes,
      selectedConnection,
      isNewTable,
      sinkConfigFields,
      canCreateTable,
    } = this.props;
    const meta = connectionMetas[selectedConnection];
    const isLoadingTree =
      (isEmpty(this.props.probeData) && isLoadingProbeData) ||
      (type === 'format' && !selectedTable) ||
      // After selecting table coming back again, then showing loading tables for <Selected Database>
      // instead of showing database doesn't have any tables.
      (type === 'configure' &&
        !isEmpty(this.props.probeData) &&
        isEmpty(databases) &&
        selectedTable);
    const destName = addedDataSink?.name || sinkConfig?.name;
    const destDesc = addedDataSink?.description || sinkConfig?.description;

    const resourceConfig = this.getResourceConfig();

    return (
      <div className="database-destination-configurator">
        <div className="main-content">
          <HeaderMetaConfiguration
            name={destName}
            nexsetId={dataSet?.id}
            isDestination
            onChange={this.onChange}
            description={destDesc}
            connection={selectedConnection}
            nexsetChipChildren={
              <ResourcePopoverMenu
                isDestination
                nexsetId={dataSet?.id}
                resourceConfig={resourceConfig}
                connectorType={selectedConnection}
                jsonConfigOnSave={this.dispatchAdvancedModeSave}
                onSwitchUiMode={this.onSwitchUiMode}
                jsonConfigOnClose={() => {
                  if (this.props.goBack) {
                    this.props.goBack();
                  }
                }}
                disabledActions={['view-json-config']}
              />
            }
            sourceOrDestinationChipChildren={
              <ResourcePopoverMenu
                isDestination
                resourceConfig={resourceConfig}
                connectorType={selectedConnection}
                jsonConfigOnSave={this.dispatchAdvancedModeSave}
                onSwitchUiMode={this.onSwitchUiMode}
                jsonConfigOnClose={() => {
                  if (this.props.goBack) {
                    this.props.goBack();
                  }
                }}
                disabledActions={['view-nexset-details']}
              />
            }
          />

          <Stack width="100%" className="configuration">
            <SearchableProvider>
              {(onChangeSearch) => (
                <NavScroller
                  navOptions={Boolean(selectedTable) ? fullNavOptions : initNavOptions}
                  navClassName="nav-bar-configuration"
                  containerClassName="nav-container-configuration"
                >
                  <NavScrollerContext.Consumer>
                    {(consumer) => {
                      return (
                        <>
                          <ResizableHorizontalLayout
                            initialSizes={['75%', '25%']}
                            minSizes={[450, 300]}
                          >
                            <Box
                              className={classnames({
                                box: true,
                                'left-part': true,
                                'box-focused': consumer?.activeScroll === 'skin-destination-table',
                              })}
                              title={
                                <Typography>
                                  <b>Destination table</b>
                                  <Typography marginLeft={2} variant="bodySmall">
                                    Select the destination table for storing all processed and
                                    transformed data files.
                                  </Typography>
                                </Typography>
                              }
                              rightView={
                                <>
                                  <span
                                    id="skin-destination-table"
                                    className="hidden-source-element"
                                  />
                                  <ExpandableSearchBar onChange={onChangeSearch} />
                                </>
                              }
                            >
                              <Stack flex={1} width={'100%'} minHeight="440px">
                                <div className="locator-container">
                                  <div className="locator">
                                    <Typography variant="bodySmall">Table selected</Typography>
                                    <div className="locator-content">
                                      <DatabaseIcon />
                                      {selectedDatabaseId && (
                                        <Typography
                                          data-testid="locator-database-name"
                                          variant="body1"
                                        >
                                          {databases[selectedDatabaseId]?.name}
                                        </Typography>
                                      )}
                                      {selectedTable && <ChevronRight />}
                                      {selectedTable && (
                                        <Stack direction="row" alignItems="center">
                                          <TableIcon />
                                          <Typography
                                            data-testid="locator-table-name"
                                            variant="body1"
                                          >
                                            {selectedTable.name}
                                          </Typography>
                                        </Stack>
                                      )}
                                    </div>
                                  </div>
                                  {isLoadingProbeData ? (
                                    <div className="refresh-loader-container">
                                      <CircularProgress
                                        size={19}
                                        sx={{
                                          color: 'currentColor',
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <RefreshIcon
                                      className="refresh-icon"
                                      fontSize="large"
                                      onClick={() => this.props.onRequestProbeTree()}
                                    />
                                  )}
                                </div>
                                {this.props.probeError ? (
                                  <Stack padding="40px">
                                    <EmptyView
                                      title="There was an error loading resources"
                                      message="If you want, you can still configure your sink using advanced mode"
                                      actionTitle="Try Again"
                                      actionProps={{
                                        onClick: this.onRequestTree,
                                      }}
                                    />
                                  </Stack>
                                ) : null}
                                {isLoadingTree ? (
                                  <div className="select-bucket-tree-list-loading">
                                    <CircularProgress />
                                    Connecting to {meta?.displayName}...
                                  </div>
                                ) : type === 'configure' &&
                                  this.props.probeData &&
                                  !isEmpty(databases) ? (
                                  <Stack flex="1" overflow="auto" maxHeight="fit-content">
                                    <DatabaseList
                                      credentialId={this.props.selectedCredentialId}
                                      databases={map(databasesIds, (i) => databases[i])}
                                      selectedDatabaseId={selectedDatabaseId}
                                      selectDatabase={this.selectDatabase}
                                      tablesForDatabase={tablesForDatabase}
                                      tables={tables}
                                      openTableEditor={this.openTableEditor}
                                      expandedTableId={expandedTableId}
                                      expandTable={this.expandTable}
                                      selectedTable={selectedTable || {}}
                                      isNewTable={isNewTable}
                                      probeDataFlat={probeDataFlat}
                                      handleShowPreview={this.handleShowPreview}
                                    />
                                  </Stack>
                                ) : null}

                                {type === 'configure' &&
                                  this.props.probeData &&
                                  isEmpty(databases) && (
                                    <TableListWithProbing
                                      credentialId={this.props.selectedCredentialId}
                                      tables={map(tablesIds, (id) => tables[id])}
                                      openTableEditor={this.openTableEditor.bind(this)}
                                      expandTable={this.expandTable.bind(this)}
                                      expandedTableId={expandedTableId}
                                      selectedTable={selectedTable || {}}
                                      isNewTable={isNewTable}
                                      probeDataFlat={probeDataFlat}
                                      canCreateTable={canCreateTable}
                                      handleShowPreview={this.handleShowPreview}
                                    />
                                  )}
                              </Stack>
                            </Box>

                            <div className="preview-box right-part">
                              <div className="preview-box-content">
                                <Typography
                                  variant="bodyLarge"
                                  className="preview-box-title"
                                  style={{
                                    ...(this.state.showPreview && {
                                      marginBottom: 0,
                                    }),
                                  }}
                                >
                                  {!this.state.showPreview ? (
                                    'DB Preview'
                                  ) : (
                                    <>Previewing {selectedTableForPreview}</>
                                  )}
                                </Typography>
                                {!this.state.showPreview && (
                                  <Typography
                                    variant="bodyDefault"
                                    className="preview-box-description"
                                  >
                                    Preview data will appear here
                                  </Typography>
                                )}
                              </div>

                              {this.state.showPreview && (
                                <ModalPreviewSamples
                                  connectionCategory={CONNECTION_CATEGORY_DATABASE}
                                  credentialId={this.props.selectedCredentialId}
                                  setLoading={this.handleLoadingPreview}
                                  selectedTable={selectedTable}
                                />
                              )}
                            </div>
                          </ResizableHorizontalLayout>

                          <div id="skin-update-mode">
                            <TableUpdateMode
                              table={selectedTable || {}}
                              editSelectedTable={this.editSelectedTable}
                              sinkConfig={this.props.sinkConfig}
                              connectionMeta={meta}
                              insertModeField={insertModeField}
                              containerClassName={
                                consumer?.activeScroll === 'skin-update-mode' ? 'box-focused' : ''
                              }
                              onTrackerModeChanged={this.onTrackerModeChanged}
                              onTrackerNameChanged={this.onTrackerNameChanged}
                              trackerMode={get(this.props.sinkConfig, 'mapping.tracker_mode')}
                              selectedConnection={selectedConnection}
                              onAutoModeChanged={this.onAutoModeChanged}
                            />
                          </div>

                          {selectedTable ? (
                            <Box
                              id="skin-format-table"
                              className={
                                consumer?.activeScroll === 'skin-format-table'
                                  ? 'box box-focused'
                                  : 'box'
                              }
                              title="Format Table"
                            >
                              <Stack padding="16px">
                                <TableEditor
                                  flowType={flowType}
                                  addedDataSink={addedDataSink}
                                  currentOrg={currentOrg}
                                  sinkConfigFields={sinkConfigFields}
                                  insertModeField={insertModeField}
                                  dataTypes={dbDataTypes}
                                  table={selectedTable || {}}
                                  dataSchema={dataSchema?.map?.((s) => ({
                                    value: s.__txName,
                                    name: s.__name,
                                    description: s.__description,
                                    type: s.__formatDisplay || s.__type,
                                  }))}
                                  sinkConfig={this.props.sinkConfig}
                                  isNew={isNewTable}
                                  editSelectedTable={this.editSelectedTable.bind(this)}
                                  editSelectedTableColumns={this.editSelectedTableColumns.bind(
                                    this,
                                  )}
                                  addSelectedTableColum={this.addSelectedTableColum.bind(this)}
                                  removeSelectedTableColum={this.removeSelectedTableColum.bind(
                                    this,
                                  )}
                                  clearColumnAttribute={this.clearColumnAttribute.bind(this)}
                                  tableNotFound={this.state.tableNotFound}
                                  selectedConnection={selectedConnection}
                                  connectionMeta={meta}
                                  onSinkConfigChanged={this.props.onSinkConfigChanged}
                                />
                              </Stack>
                            </Box>
                          ) : null}
                        </>
                      );
                    }}
                  </NavScrollerContext.Consumer>
                </NavScroller>
              )}
            </SearchableProvider>
          </Stack>
        </div>
        {configFields.length > 0 && (
          <AdvancedConfiguration
            id={this.props.id}
            sinkConfigFields={configFields}
            sinkConfig={this.props.sinkConfig}
            onSourceConfigChanged={this.props.onSinkConfigChanged}
            resourceMode={'sink'}
            isModal={true}
            connectionType={selectedConnection}
            connectionCategory={CONNECTION_CATEGORY_DATABASE}
          />
        )}
      </div>
    );
  }
}

export default DatabaseConfigurator;
